import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	Button,
	Card,
	CardBody,
	CardHeader,
	CardFooter,
	UncontrolledDropdown,
	DropdownToggle,
	DropdownMenu,
	DropdownItem,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import moment from 'moment';
import 'moment/locale/es';

import BarChart from '../../../../../Components/Common/Charts/BarChart';
import { generateColorGradient } from '../../../../../Components/Hooks/useColorArrayHooks';
import { getHourMeterSummary } from '../../../../../slices/thunks';
import { getConsumptionByEstablishment } from '../../../../../slices/fuelSupply/thunk';

moment.locale('es');

// ─── Helpers ────────────────────────────────────────────────────────────

const formatMap = {
	'2A': 'YYYY',
	'1A': 'YYYY',
	'6M': 'MM-YYYY',
	'3M': 'MM-YYYY',
	'1M': 'YYYY-[S]WW',
	AC: 'MM-YYYY',
};

const formatDate = (date, filter) => {
	return moment(date).format(formatMap[filter] || 'DD-MM-YYYY');
};

const getUniqueUnits = data => {
	if (!Array.isArray(data) || data.length === 0) return 'N/A';
	const uniqueUnits = new Set(
		data
			.map(item => item.unit_measurement_info?.name?.trim())
			.filter(Boolean)
			.map(unit => unit.charAt(0).toUpperCase() + unit.slice(1))
	);
	return uniqueUnits.size > 0 ? Array.from(uniqueUnits).join(', ') : 'N/A';
};

const tooltipFormatter = ({ series, seriesIndex, dataPointIndex, w }) => {
	const category = w.globals.labels[dataPointIndex];
	const generatorName = w.config.series[seriesIndex].name;
	const amount = w.config.series[seriesIndex].data[dataPointIndex].y;
	const unit = w.config.yaxis[0]?.title?.text || 'N/A';
	return `
        <div style="border-radius: 8px; padding: 10px; font-size: 11px; text-align: left; min-width: 200px; background: #fff; box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.1);">
            <div><strong>Fecha:</strong> ${category}</div>
            <div><strong>Generador:</strong> ${generatorName}</div>
            <div><strong>Consumo:</strong> ${amount.toLocaleString()} ${unit}</div>
        </div>
    `;
};

const prepareChartDataPower = (data, colorsChart, filter) => {
	const dateFormat = formatMap[filter] || 'DD-MM-YYYY';

	const groupedData = data.reduce((acc, item) => {
		if (!item.register_date || !item.power_generation_source_info) return acc;
		const date = formatDate(item.register_date, filter);
		const generatorName =
			item.power_generation_source_info.name || 'Generador Desconocido';
		const serialNumber =
			item.power_generation_source_info.serial_number || 'SN-Desconocido';
		const fuelType = item.fuel_type_info?.name || 'Desconocido';
		const bargeName = item.feeding_barges_info?.name?.trim() || '';
		const generatorKey = bargeName
			? `${bargeName} - ${generatorName}`
			: generatorName;
		const key = `${date}-${generatorKey}`;
		const consumption = Number(item.consumption) || 0;

		if (!acc[key]) {
			acc[key] = {
				date,
				generatorKey,
				serialNumber,
				fuelType,
				bargeName,
				generatorName,
				consumption: 0,
			};
		}
		acc[key].consumption += consumption;
		return acc;
	}, {});

	const sortedData = Object.values(groupedData).sort((a, b) =>
		moment(a.date, dateFormat).diff(moment(b.date, dateFormat))
	);
	const categories = [...new Set(sortedData.map(item => item.date))];
	const generators = [
		...new Set(sortedData.map(item => item.generatorKey)),
	].sort((a, b) => a.localeCompare(b));

	let generatorColorMap = {};
	if (colorsChart) {
		generatorColorMap = generators.reduce((acc, generator, index) => {
			acc[generator] = colorsChart[index % colorsChart.length];
			return acc;
		}, {});
	}
	const series = generators.map(generatorKey => ({
		name: generatorKey,
		data: categories.map(date => {
			const record = sortedData.find(
				item => item.date === date && item.generatorKey === generatorKey
			);
			return record
				? { x: date, y: record.consumption, fuelType: record.fuelType }
				: { x: date, y: 0, fuelType: 'Desconocido' };
		}),
	}));

	return {
		series,
		categories,
		colors: colorsChart
			? generators.map(g => generatorColorMap?.[g])
			: undefined,
	};
};

const prepareChartDataConsumption = (data, colorsChart, filter) => {
	const dateFormat = formatMap[filter] || 'DD-MM-YYYY';

	const groupedData = data.reduce((acc, item) => {
		if (
			!item.supply_date ||
			!item.fuel_type_info ||
			!item.unit_measurement_info
		)
			return acc;
		const date = formatDate(item.supply_date, filter);
		const fuelType = item.fuel_type_info?.name || 'Desconocido';
		const unit = item.unit_measurement_info?.name || 'N/A';
		const key = `${date}-${fuelType}-${unit}`;
		const amount = Number(item.amount) || 0;

		if (!acc[key]) {
			acc[key] = {
				date,
				fuelType,
				unit,
				amount: 0,
				extra: {
					tankName: item.tank_name || 'Desconocido',
					barge: item.barge || 'No aplica',
					stock: item.stock || 0,
					capacity: item.capacity || 0,
					fuelType,
					unit,
				},
			};
		}
		acc[key].amount += amount;
		return acc;
	}, {});

	const sortedData = Object.values(groupedData).sort((a, b) =>
		moment(a.date, dateFormat).diff(moment(b.date, dateFormat))
	);
	const categories = [...new Set(sortedData.map(item => item.date))];
	const fuelTypes = [
		...new Set(sortedData.map(item => `${item.fuelType} (${item.unit})`)),
	];
	let fuelTypeColorMap = {};
	if (colorsChart) {
		fuelTypeColorMap = fuelTypes.reduce((acc, ft, i) => {
			acc[ft] = colorsChart[i % colorsChart.length];
			return acc;
		}, {});
	}
	const series = fuelTypes.map(ft => ({
		name: ft,
		data: categories.map(date => {
			const record = sortedData.find(
				item =>
					item.date === date && `${item.fuelType} (${item.unit})` === ft
			);
			return record
				? { x: date, y: record.amount, extra: record.extra }
				: { x: date, y: 0, extra: {} };
		}),
	}));

	return {
		series,
		categories,
		colors: colorsChart
			? fuelTypes.map(f => fuelTypeColorMap?.[f])
			: undefined,
	};
};

// ─── Componente Principal ────────────────────────────────────────────────

const FuelBalancePowerGeneration = ({
	establishment,
	title,
	legendPosition,
	option,
	um = '',
	minHeight,
	maxHeight,
}) => {
	const dispatch = useDispatch();
	const [selectedFilter, setSelectedFilter] = useState(option);
	const [selectedGroup, setSelectedGroup] = useState('power');

	// Power data
	const selectorPower = createSelector(
		state => state.FuelSupply,
		(_, type) => type,
		(data, type) => ({ info: data.hourmeterSummary[type] || null })
	);
	const { info: infoPower } = useSelector(state =>
		selectorPower(state, 'power_consumption')
	);

	const [chartDataPower, setChartDataPower] = useState({
		series: [],
		categories: [],
		colors: [],
	});
	const yAxisPower = getUniqueUnits(infoPower?.data);

	// Consumption data
	const selectorConsumption = createSelector(
		state => state.FuelSupply,
		(_, type) => type,
		(data, type) => ({ info: data.fuelSupplyByEstablishment[type] || null })
	);
	const { info: infoConsumption } = useSelector(state =>
		selectorConsumption(state, 'balance')
	);
	const [chartDataConsumption, setChartDataConsumption] = useState({
		series: [],
		categories: [],
		colors: [],
	});
	const yAxisConsumption = getUniqueUnits(infoConsumption?.data);

	// Fetch data
	useEffect(() => {
		if (!establishment?._id) return;
		if (selectedGroup === 'power') {
			dispatch(
				getHourMeterSummary({
					establishment_id: establishment._id,
					filter: selectedFilter,
					type: 'power_consumption',
				})
			);
		} else {
			dispatch(
				getConsumptionByEstablishment({
					establishment_id: establishment._id,
					filter: selectedFilter,
					type: 'balance',
				})
			);
		}
	}, [establishment, selectedFilter, selectedGroup]);

	// Prepare chart data
	useEffect(() => {
		if (selectedGroup === 'power' && Array.isArray(infoPower?.data)) {
			setChartDataPower(
				prepareChartDataPower(infoPower.data, undefined, selectedFilter)
			);
		} else if (
			selectedGroup === 'comsumption' &&
			Array.isArray(infoConsumption?.data)
		) {
			setChartDataConsumption(
				prepareChartDataConsumption(
					infoConsumption.data,
					undefined,
					selectedFilter
				)
			);
		}
	}, [infoPower?.data, infoConsumption?.data, selectedFilter, selectedGroup]);

	const handleFilterChange = filter => {
		setSelectedFilter(filter);
	};

	const handleGroupChange = group => {
		setSelectedGroup(group);
	};

	const noData =
		selectedGroup === 'power'
			? infoPower?.data?.length === 0
			: infoConsumption?.data?.length === 0;

	const currentChartData =
		selectedGroup === 'power' ? chartDataPower : chartDataConsumption;
	const currentYAxis =
		selectedGroup === 'power' ? yAxisPower : yAxisConsumption;

	return (
		<Card className='m-0 h-100'>
			<CardHeader className='p-2 align-items-center d-flex'>
				<h5 className='card-title mb-0 flex-grow-1'>{title}</h5>
				<UncontrolledDropdown className='card-header-dropdown me-2'>
					<DropdownToggle
						tag='a'
						className='text-reset dropdown-btn'
						role='button'
					>
						<span className='fs-11'>Agrupado por: </span>
						<span className='text-muted fw-bold text-uppercase'>
							{selectedGroup === 'power'
								? 'Fuente de Generación de Energía'
								: 'Consumo General'}
							<i className='mdi mdi-chevron-down ms-1'></i>
						</span>
					</DropdownToggle>
					<DropdownMenu className='dropdown-menu-end'>
						<DropdownItem
							onClick={() => handleGroupChange('comsumption')}
							active={selectedGroup === 'comsumption'}
						>
							Consumo General
						</DropdownItem>
						<DropdownItem
							onClick={() => handleGroupChange('power')}
							active={selectedGroup === 'power'}
						>
							Fuente de Generación de Energía
						</DropdownItem>
					</DropdownMenu>
				</UncontrolledDropdown>
				<div className='btn-box-dashboard'>
					{['2A', '1A', '6M', '3M', '1M'].map(f => (
						<Button
							key={f}
							color={selectedFilter === f ? 'primary' : 'secondary'}
							size='sm'
							className={`btn-soft-${
								selectedFilter === f ? 'primary' : 'secondary'
							} me-1`}
							onClick={() => handleFilterChange(f)}
						>
							{f}
						</Button>
					))}
				</div>
			</CardHeader>

			{noData ? (
				<>
					<CardBody className='p-2'>
						<div className='d-flex justify-content-center align-items-center p-3'>
							<div className='text-center'>
								<div className='avatar-md'>
									<span className='avatar-title bg-info-subtle rounded-circle fs-1'>
										<i className='ri-gas-station-fill text-info'></i>
									</span>
								</div>
							</div>
						</div>
					</CardBody>
					<CardFooter className='m-0 p-2 text-center'>
						<h6 className='mb-0 p-0 text-wrap w-100'>
							<span className='fs-11 badge bg-info-subtle text-info text-wrap'>
								No cuenta con registro de consumo de combustible
							</span>
						</h6>
					</CardFooter>
				</>
			) : (
				<CardBody className='p-0'>
					<BarChart
						seriesChart={currentChartData.series}
						categoriesChart={currentChartData.categories}
						toolbarChart={true}
						height={'100%'}
						minHeight={minHeight}
						maxHeight={maxHeight}
						legendPosition={legendPosition}
						legendOffsetY={2}
						strokeCurve={'smooth'}
						chartType={'bar'}
						horizontal={false}
						yAxisTitle={currentYAxis}
						um={um}
						tooltipFormatter={
							selectedGroup === 'power' ? tooltipFormatter : undefined
						}
						dataLabels={false}
					/>
				</CardBody>
			)}
		</Card>
	);
};

export default FuelBalancePowerGeneration;
